import { useQuery } from "@tanstack/react-query";
import {
  CHECKOUT_ROUTES,
  ORDER_ROUTES,
  PACKAGE_ROUTES,
  orderCheckoutResponseSchema,
  packageCheckoutResponseSchema,
  packageListResponseSchema,
  type Currency,
  type PackageCheckout,
  type PaymentMethod,
  type CheckoutShippingSelection,
  type ShippingAddress,
} from "@nh/shared";
import type { PackageList } from "@/features/packages/packages.api";
import { apiFetch } from "@/shared/api/client";
import { authFetch } from "@/shared/api/session";
import type { CheckoutResult } from "@/features/cart/cart.checkout";

/**
 * Los PAQUETES MAYORISTAS del modal de "añade tu primer stock". El
 * listado público esconde la categoría `franchisee` a propósito —no son
 * paquetes de la sección personal—, así que aquí se pide con nombre y
 * apellido.
 */
export function fetchWholesalePackages(): Promise<PackageList> {
  return apiFetch(
    `${PACKAGE_ROUTES.publicList}?category=franchisee&perPage=100`,
    packageListResponseSchema,
    { baseUrl: "" },
  );
}

export function useWholesalePackages() {
  return useQuery<PackageList>({
    queryKey: ["packages", "wholesale"],
    queryFn: fetchWholesalePackages,
  });
}

/**
 * Adquirir la franquicia: UN pedido con dos renglones — la franquicia y
 * el primer stock. El servidor pone los dos precios; de aquí solo viaja
 * qué paquete, en qué moneda y a qué teléfono contactar.
 */
export function createFranchiseOrder(input: {
  currency: Currency;
  packageId: string;
  phone: string;
  /** `false` compra SOLO el paquete, sin adquirir la franquicia. */
  withFranchise: boolean;
  paymentMethod: PaymentMethod;
  shippingAddress: ShippingAddress;
  shippingSelection?: CheckoutShippingSelection;
}): Promise<CheckoutResult> {
  return authFetch(ORDER_ROUTES.franchiseCheckout, orderCheckoutResponseSchema, {
    baseUrl: "",
    method: "POST",
    body: input,
  });
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * "Comprar directo": el paquete solo, pagado con Stripe (Anahí, 29-ago).
 *
 * La salida de la derecha en `/franquicias/adquirir`, para quien no
 * quiere la franquicia. NO es el mismo camino que `createFranchiseOrder`:
 * aquel abre un pedido nuestro que alguien valida a mano; este devuelve
 * un link de Stripe al que se manda el navegador y se cobra ahí.
 *
 * De aquí viaja el ID del paquete y la moneda. El precio lo pone el
 * servidor leyendo la fila del catálogo — ver `checkout.service.ts`.
 */
export function createPackageCheckout(input: {
  currency: Currency;
  packageId: string;
}): Promise<PackageCheckout> {
  return authFetch(CHECKOUT_ROUTES.package, packageCheckoutResponseSchema, {
    baseUrl: "",
    method: "POST",
    body: input,
  });
}
